import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, normalizeStyle as _normalizeStyle, createBlock as _createBlock, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-4781fcfa"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["onMouseover"]
const _hoisted_2 = ["onClick"]
const _hoisted_3 = ["onMouseover"]
const _hoisted_4 = ["onClick"]
const _hoisted_5 = ["onMouseover"]
const _hoisted_6 = ["onClick"]

import { useRouter } from "vue-router";
import { RouteNames } from "@/router/RouteNames";
import GridBase from "@/components/ui/grid/grid-base.vue";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import { MaintenanceRequestItem } from "@/types/maintenance-requests/MaintenanceRequestItem";
import displayErrorMessage from "@/utils/ErrorHandling";
import { getErrorMessage } from "bright-logic-core/src/errors";
import { DxColumn } from "devextreme-vue/data-grid";
import { computed, inject, onMounted, ref, watch } from "vue";
import { ContextMenuItem } from "@/types/ui/menus/ContextMenuItem";
import FindContactModal from "@/components/find/contacts/find-contact-modal.vue";
import FindPropertyModal from "@/components/find/properties/find-property-modal.vue";
import MaintenanceRequestListSummary from "@/components/maintenance-requests/maintenance-request-list-summary.vue";
import MaintenanceRequestModal from "@/components/maintenance-requests/maintenance-request-item-modal.vue";
import { displayQuestionAlert } from "@/alerts/questionAlert";
import { MaintenanceRequestListFilters } from "@/types/maintenance-requests/MaintenanceRequestListFilters";
import { MaintenanceRequestStatus } from "@/enums/maintenance-requests/MaintenanceRequestStatus";
import { useMaintenanceRequestListFiltersStore } from "@/stores/lists/MaintenanceRequestListStore";
import { useUserPermissionsStore } from "@/stores/UserPermissionsStore";
import { UserPermissions } from "@/types/user/permissions/UserPermissions";
import { ContactFindResult } from "@/types/find/contacts/ContactFindResult";
import { PropertyFindResult } from "@/types/find/properties/PropertyFindResult";
import { MaintenanceRequests } from "acquaint-api/models/MaintenanceRequests";
import WorkOrderModal from "@/components/work-orders/work-order-modal.vue";
import EmailFormModal from "@/components/email/email-form-modal.vue";
import { Templates } from "@/enums/constants/Templates";
import { CurrentUser } from "@/types/user/CurrentUser";
import { useCurrentUserStore } from "@/stores/CurrentUserStore";
//@ts-ignore
import { debounce } from "lodash-es";


export default /*#__PURE__*/_defineComponent({
  __name: 'maintenance-request-list',
  props: {
  /**
   * The height of grid.
   * @type {String}
   * @default 80vh
   */
  gridHeight: {
    type: String,
    default: "81vh",
  },
  /**
   * Determines whether the header should be dark.
   * @type {boolean}
   * @default true
   */
  darkHeader: {
    type: Boolean,
    default: true,
  },
  /**
   * Automatically increased number to force reload
   * @type {Number}
   * @default 0
   */
  forceReload: {
    type: Number,
    default: 0,
  },
  /**
   * Whether modal called from notification popup.
   * @type {boolean}
   * @required
   */
  fromNotificationPopup: {
    type: Boolean,
    required: false,
  },
  isFromOpenModal: {
    type: Boolean,
    default: false,
  },
},
  setup(__props) {

const props = __props;


const apiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const filtersStore = useMaintenanceRequestListFiltersStore();
const currentUserStore = useCurrentUserStore();
const currentUserDetails = ref<CurrentUser>();
const router = useRouter();
const screenSize = ref(screen.width);
const loading = ref(false);
const exportGrid = ref(false);
const displayFindContactModal = ref(false);
const displayFindPropertyModal = ref(false);
const displayNewWorkOrderModal = ref(false);
const displayEmailFormModal = ref(false);
const displayMaintenanceRequestModal = ref(false);
const dbMaintenanceRequests = ref<MaintenanceRequests>();
const maintenanceRequestsData = ref<MaintenanceRequestItem[]>([]);
const selectedRecord = ref<MaintenanceRequestItem>();
const userPermissionStore = useUserPermissionsStore();
const userPermissions = ref<UserPermissions>();
const propertyCellRowId = ref(0);
const contactCellRowId = ref(0);
const statusCellRowId = ref(0);
const summaryFilters = ref<MaintenanceRequestListFilters>({
  status: {
    id: MaintenanceRequestStatus.Outstanding,
    description: "Outstanding",
  },
  branch: { id: -1, description: "All" },
});

onMounted(async () => {
  currentUserDetails.value = await currentUserStore.getCurrentUserDetails();
  userPermissions.value = await userPermissionStore.getUserPermissions();

  /* Attempt to read previous filters from cache.
   * This will save users time and potentially aid app performance */
  const cachedFilters = await filtersStore.getFilters();

  if (cachedFilters && !props.fromNotificationPopup) {
    summaryFilters.value = { ...cachedFilters };
  } else {
    fetchMaintenanceRequests();
  }
});

watch(
  () => summaryFilters.value,
  async () => {
    // Update cached filters so filters are remembered next time the user loads the property list
    await filtersStore.updateFilters(summaryFilters.value);

    // Reset selected id so floating action button disappears
    selectedRecord.value = undefined;

    await fetchMaintenanceRequests();
  },
);

/** Maps summary filters to filters model supported by the api endpoint */
const apiFilters = computed(() => {
  const filters = { status: -1, branch: -1 };

  if (summaryFilters.value) {
    filters.status = summaryFilters.value.status?.id ?? -1;
    filters.branch = summaryFilters.value.branch?.id ?? -1;
  }

  return filters;
});

const rowCount = computed(() => maintenanceRequestsData.value.length);

const menuItems = computed(() => {
  let menu: ContextMenuItem[] = [];

  if (selectedRecord.value) {
    menu.unshift(
      {
        text: "Edit",
        icon: "fas fa-pencil",
        key: "edit",
        disabled: !userPermissions.value?.correspondence.edit,
      },
      {
        text: "Set Contact",
        icon: "fas fa-user",
        key: "setContact",
        disabled: selectedRecord.value.contactId > 0,
      },
      {
        text: "Set Property",
        icon: "fas fa-house",
        key: "setProperty",
        disabled: selectedRecord.value.propertyId > 0,
      },
      {
        text: "Create Work Order",
        icon: "fa-solid fa-screwdriver-wrench",
        key: "createWorkOrder",
        disabled: !(
          selectedRecord.value.status !== "Actioned" &&
          userPermissions.value?.propertyManagementConfiguration.new
        ),
      },
      {
        text: "Reject Request",
        icon: "fas fa-trash",
        key: "rejectRequest",
        disabled: !(
          selectedRecord.value.status === "Outstanding" &&
          userPermissions.value?.propertyManagementConfiguration.delete
        ),
      },
      {
        text: "Remove Contact",
        icon: "fas fa-trash",
        key: "removeContact",
        disabled: selectedRecord.value.contactId === 0,
      },

      {
        text: "Remove Property",
        icon: "fas fa-trash",
        key: "removeProperty",
        disabled: selectedRecord.value.propertyId === 0,
      },
      {
        text: "Set as Outstanding",
        icon: "fas fa-thumbtack",
        key: "setOutstanding",
        disabled: selectedRecord.value.status === "Outstanding",
      },
    );
  }

  return menu;
});

const menuItemClicked = async (key: string) => {
  let question;
  switch (key) {
    case "edit":
      displayMaintenanceRequestModal.value = true;
      break;
    case "setContact":
      displayFindContactModal.value = true;
      break;
    case "removeContact":
      question = await displayQuestionAlert(
        "Are you sure you want to remove these details?",
      );

      if (!question.isConfirmed) {
        return;
      }
      if (selectedRecord.value) {
        selectedRecord.value = {
          ...selectedRecord.value,
          contactId: 0,
        };
        await updateMaintenanceRequests(selectedRecord.value);
      }
      break;
    case "setProperty":
      displayFindPropertyModal.value = true;
      break;
    case "removeProperty":
      question = await displayQuestionAlert(
        "Are you sure you want to remove these details?",
      );

      if (!question.isConfirmed) {
        return;
      }
      if (selectedRecord.value) {
        selectedRecord.value = {
          ...selectedRecord.value,
          propertyId: 0,
        };
        await updateMaintenanceRequests(selectedRecord.value);
      }
      break;
    case "setOutstanding":
      question = await displayQuestionAlert(
        "Are you sure you want to do this?",
      );

      if (!question.isConfirmed) {
        return;
      }

      if (selectedRecord.value) {
        selectedRecord.value = {
          ...selectedRecord.value,
          status: MaintenanceRequestStatus.Outstanding.toString(),
        };
        await updateMaintenanceRequests(selectedRecord.value);
      }
      break;
    case "createWorkOrder":
      question = await displayQuestionAlert(
        "Are you sure you want to do this?",
      );

      if (!question.isConfirmed) {
        return;
      }
      await getMaintenanceRequests();
      displayNewWorkOrderModal.value = true;
      break;
    case "rejectRequest":
      if (selectedRecord.value) {
        question = await displayQuestionAlert(
          "Are you sure you want to do this?",
        );

        if (!question.isConfirmed) {
          return;
        }
        if (selectedRecord.value.contactId > 0) {
          question = await displayQuestionAlert(
            "Would you like to email the contact who submitted the Maintenance Request?",
          );

          if (question.isConfirmed) {
            displayEmailFormModal.value = true;
          }
        } else {
          await setMaintenanceRequestStatusToRejected(selectedRecord.value.id);
        }
      }
      break;
    case "refresh":
      await fetchMaintenanceRequests();
      break;
    case "export":
      exportGrid.value = true;
      break;
  }
};

watch(
  () => props.forceReload,
  async () => {
    selectedRecord.value = undefined;
    await fetchMaintenanceRequests();
  },
  { immediate: false },
);

const fetchMaintenanceRequests = debounce(async () => {
  loading.value = true;
  try {
    maintenanceRequestsData.value = await apiClient.httpPost(
      `app/maintenancerequests/list`,
      apiFilters.value,
    );
    return true;
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    maintenanceRequestsData.value = [];
    return false;
  } finally {
    loading.value = false;
  }
}, 300);

const getMaintenanceRequests = async () => {
  try {
    dbMaintenanceRequests.value = await apiClient.httpGet(
      `database/maintenancerequests/${selectedRecord.value?.id}`,
    );
    return true;
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    return false;
  }
};

const updateMaintenanceRequests = async (item: MaintenanceRequestItem) => {
  if (!item) return false;

  await getMaintenanceRequests();

  if (!dbMaintenanceRequests.value) return false;

  const updateItem = toDbMaintenanceRequests(item, dbMaintenanceRequests.value);

  try {
    await apiClient.httpPut(
      `database/maintenancerequests/${selectedRecord.value?.id}`,
      updateItem,
    );
    await updateMaintenanceRequestsData();
    return true;
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    return false;
  }
};

const updateMaintenanceRequestsData = async () => {
  try {
    const maintenancerequests = await apiClient.httpPost<
      MaintenanceRequestItem[]
    >(`app/maintenancerequests/list`, apiFilters.value);

    maintenanceRequestsData.value = [...maintenancerequests];

    return true;
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    return false;
  }
};

const setMaintenanceRequestStatusToRejected = async (id: number) => {
  try {
    await apiClient.httpGetVoid(`app/maintenancerequests/${id}/SetAsRejected`);
    await updateMaintenanceRequestsData();
    return true;
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    return false;
  }
};

const toDbMaintenanceRequests = (
  item: MaintenanceRequestItem,
  original: MaintenanceRequests,
): MaintenanceRequests => {
  return {
    ...original,
    propertyID: item.propertyId,
    contactID: item.contactId,
    status: mapStatusToByte(item.status),
  };
};

const mapStatusToByte = (status: string): number => {
  switch (status) {
    case "Outstanding":
      return MaintenanceRequestStatus.Outstanding;
    case "Actioned":
      return MaintenanceRequestStatus.ConvertedToWorkOrder;
    case "Rejected":
      return MaintenanceRequestStatus.Rejected;
    default:
      return parseInt(status);
  }
};

const onRowClick = async (e: any) => {
  if (!e) {
    selectedRecord.value = undefined;
    return;
  }

  selectedRecord.value = e;
  displayMaintenanceRequestModal.value = true;
};

const onRowSelected = (e: any) => {
  if (!e) {
    selectedRecord.value = undefined;
    return;
  }

  selectedRecord.value = e;
};

const onFiltersChanged = async () => {
  await fetchMaintenanceRequests();
};

const gridHeight = computed(() => {
  if (screenSize.value > 768) return `height: ${props.gridHeight};`;
  return "";
});

const navigateToProperty = (propertyId: number) => {
  router.push({
    name: RouteNames.PropertyPage,
    params: { propertyid: propertyId.toString() },
  });
};

const displayPropertyIcon = (id: number) => {
  propertyCellRowId.value = id;
};

const hidePropertyIcon = () => {
  propertyCellRowId.value = 0;
};
const navigateToContact = (contactId: number) => {
  router.push({
    name: RouteNames.ContactPage,
    params: { contactid: contactId.toString() },
  });
};

const displayContactIcon = (id: number) => {
  contactCellRowId.value = id;
};

const hideContactIcon = () => {
  contactCellRowId.value = 0;
};
const navigateToWorkOrder = (workOrderId: number) => {
  router.push({
    name: RouteNames.WorkOrdersPage,
    params: { workorderid: workOrderId.toString() },
  });
};

const displayWorkOrderIcon = (id: number) => {
  statusCellRowId.value = id;
};

const hideWorkOrderIcon = () => {
  statusCellRowId.value = 0;
};

const onContactSelected = async (contact: ContactFindResult) => {
  if (selectedRecord.value && contact) {
    selectedRecord.value = { ...selectedRecord.value, contactId: contact.id };
    await updateMaintenanceRequests(selectedRecord.value);
  }
};

const onPropertySelected = async (property: PropertyFindResult) => {
  if (selectedRecord.value && property) {
    selectedRecord.value = {
      ...selectedRecord.value,
      propertyId: property.id,
    };
    await updateMaintenanceRequests(selectedRecord.value);
  }
};
const onWorkOrderCreated = async (workOrderId: number) => {
  if (selectedRecord.value) {
    await setMaintenanceRequestStatusToActioned(
      selectedRecord.value.id,
      workOrderId,
    );
    await updateMaintenanceRequestsData();
  }
};

const setMaintenanceRequestStatusToActioned = async (
  id: number,
  workOrderId: number,
) => {
  try {
    await apiClient.httpGetVoid(
      `app/maintenancerequests/${id}/workorder/${workOrderId}`,
    );
    return true;
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    return false;
  }
};

const onEmailSent = async () => {
  if (!selectedRecord.value) return;

  await setMaintenanceRequestStatusToRejected(selectedRecord.value.id);
};
const onStatusChanged = async () => {
  await updateMaintenanceRequestsData();
};

const bodyWidth = computed(() => {
  return props.isFromOpenModal ? 230 : 400;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(MaintenanceRequestListSummary, {
      "row-count": rowCount.value,
      "menu-items": menuItems.value,
      modelValue: summaryFilters.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((summaryFilters).value = $event)),
      onMenuItemClick: menuItemClicked,
      onFiltersChanged: onFiltersChanged,
      "dark-header": __props.darkHeader
    }, null, 8, ["row-count", "menu-items", "modelValue", "dark-header"]),
    _createElementVNode("div", {
      style: _normalizeStyle(gridHeight.value)
    }, [
      _createVNode(GridBase, {
        "data-source": maintenanceRequestsData.value,
        "id-column-name": "id",
        loading: loading.value,
        "show-action-button": true,
        "column-auto-width": true,
        onRowClick: onRowClick,
        onRowSelected: onRowSelected,
        onAdaptiveRowSelection: onRowSelected,
        "pre-selected-id": selectedRecord.value?.id,
        wordWrapEnabled: true,
        "run-export": exportGrid.value,
        "onUpdate:runExport": _cache[1] || (_cache[1] = ($event: any) => ((exportGrid).value = $event)),
        "custom-column-slots": ['contactCell', 'propertyCell', 'statusCell']
      }, {
        columns: _withCtx(() => [
          _createVNode(_unref(DxColumn), {
            "data-field": "date",
            "data-type": "date",
            caption: "Date",
            width: 100,
            "hiding-priority": 19,
            format: "dd/MM/yyyy"
          }),
          _createVNode(_unref(DxColumn), {
            "data-field": "subject",
            caption: "Subject",
            "hiding-priority": 18,
            width: 180
          }),
          _createVNode(_unref(DxColumn), {
            "data-field": "body",
            caption: "Body",
            "hiding-priority": 27,
            width: bodyWidth.value
          }, null, 8, ["width"]),
          _createVNode(_unref(DxColumn), {
            "data-field": "pictures",
            caption: "Pictures",
            "hiding-priority": 26,
            width: 80
          }),
          _createVNode(_unref(DxColumn), {
            "data-field": "property",
            caption: "Property",
            width: 200,
            "hiding-priority": 25
          }),
          _createVNode(_unref(DxColumn), {
            "data-field": "propertyMatch",
            caption: "Matched Property",
            width: 150,
            "hiding-priority": 24,
            "cell-template": "propertyCell"
          }),
          _createVNode(_unref(DxColumn), {
            "data-field": "contact",
            caption: "Contact",
            "hiding-priority": 23,
            width: 120,
            "cell-template": "contactCell"
          }),
          _createVNode(_unref(DxColumn), {
            "data-field": "status",
            caption: "Status",
            "hiding-priority": 22,
            width: 100,
            "cell-template": "statusCell"
          }),
          _createVNode(_unref(DxColumn), {
            "data-field": "contactNumber",
            caption: "Contact Number",
            "hiding-priority": 5,
            width: 120
          }),
          _createVNode(_unref(DxColumn), {
            "data-field": "email",
            caption: "Email",
            "hiding-priority": 4,
            width: 200
          }),
          _createVNode(_unref(DxColumn), {
            "data-field": "id",
            caption: "ID",
            "hiding-priority": 1
          })
        ]),
        propertyCellData: _withCtx(({ data }) => [
          _createElementVNode("div", {
            class: "contact-property-cell",
            onMouseover: ($event: any) => (displayPropertyIcon(data.id)),
            onMouseleave: hidePropertyIcon
          }, [
            (propertyCellRowId.value == data.id && data.propertyId > 0)
              ? (_openBlock(), _createElementBlock("i", {
                  key: 0,
                  onClick: ($event: any) => (navigateToProperty(data.propertyId)),
                  class: "fas fa-house"
                }, null, 8, _hoisted_2))
              : _createCommentVNode("", true),
            _createTextVNode(" " + _toDisplayString(data.propertyMatch), 1)
          ], 40, _hoisted_1)
        ]),
        contactCellData: _withCtx(({ data }) => [
          _createElementVNode("div", {
            class: "contact-property-cell",
            onMouseover: ($event: any) => (displayContactIcon(data.id)),
            onMouseleave: hideContactIcon
          }, [
            (contactCellRowId.value == data.id && data.contactId > 0)
              ? (_openBlock(), _createElementBlock("i", {
                  key: 0,
                  onClick: ($event: any) => (navigateToContact(data.contactId)),
                  class: "fas fa-user"
                }, null, 8, _hoisted_4))
              : _createCommentVNode("", true),
            _createTextVNode(" " + _toDisplayString(data.contact), 1)
          ], 40, _hoisted_3)
        ]),
        statusCellData: _withCtx(({ data }) => [
          _createElementVNode("div", {
            class: "contact-property-cell",
            onMouseover: ($event: any) => (displayWorkOrderIcon(data.id)),
            onMouseleave: hideWorkOrderIcon
          }, [
            (statusCellRowId.value == data.id && data.workOrderId > 0)
              ? (_openBlock(), _createElementBlock("i", {
                  key: 0,
                  onClick: ($event: any) => (navigateToWorkOrder(data.workOrderId)),
                  class: "fas fa-eye"
                }, null, 8, _hoisted_6))
              : _createCommentVNode("", true),
            _createTextVNode(" " + _toDisplayString(data.status), 1)
          ], 40, _hoisted_5)
        ]),
        _: 1
      }, 8, ["data-source", "loading", "pre-selected-id", "run-export"])
    ], 4),
    _createVNode(FindContactModal, {
      modelValue: displayFindContactModal.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((displayFindContactModal).value = $event)),
      onContactSelected: onContactSelected
    }, null, 8, ["modelValue"]),
    _createVNode(FindPropertyModal, {
      modelValue: displayFindPropertyModal.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((displayFindPropertyModal).value = $event)),
      onPropertySelected: onPropertySelected
    }, null, 8, ["modelValue"]),
    _createVNode(WorkOrderModal, {
      modelValue: displayNewWorkOrderModal.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((displayNewWorkOrderModal).value = $event)),
      maintenanceRequests: dbMaintenanceRequests.value,
      fromMaintenanceRequest: true,
      onWorkOrderCreated: onWorkOrderCreated
    }, null, 8, ["modelValue", "maintenanceRequests"]),
    _createVNode(EmailFormModal, {
      modelValue: displayEmailFormModal.value,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((displayEmailFormModal).value = $event)),
      emailTemplateId: _unref(Templates).MaintenanceRequestRejection,
      userId: currentUserDetails.value?.id,
      contactList: [selectedRecord.value?.contactId ?? 0],
      onEmailSent: onEmailSent,
      onCancelPromptAccepted: onEmailSent
    }, null, 8, ["modelValue", "emailTemplateId", "userId", "contactList"]),
    (selectedRecord.value)
      ? (_openBlock(), _createBlock(MaintenanceRequestModal, {
          key: 0,
          modelValue: displayMaintenanceRequestModal.value,
          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((displayMaintenanceRequestModal).value = $event)),
          maintenanceRequestItem: selectedRecord.value,
          onStatusChanged: onStatusChanged
        }, null, 8, ["modelValue", "maintenanceRequestItem"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})